/**
 * Fordonets laddkonfiguration: batterikapacitet, laddförlust samt hem- och
 * arbetsplats för EV-laddredovisning. Platserna väljs på karta (lat/lng sparas)
 * och adressen reverse-geokodas. Laddning utanför hem/arbete klassas som publik.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { MapPinIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import LocationPickerModal, { type PickedLocation } from './LocationPickerModal';

export interface ChargingConfigFormState {
  batteryCapacityKwh: number | null;
  chargingEfficiency: number | null;
  homeLatitude: number | null;
  homeLongitude: number | null;
  homeRadiusM: number | null;
  homeAddress: string | null;
  workLatitude: number | null;
  workLongitude: number | null;
  workRadiusM: number | null;
  workAddress: string | null;
}

interface VehicleChargingTabProps {
  value: ChargingConfigFormState;
  onChange: (next: ChargingConfigFormState) => void;
  hasChanges?: boolean;
  /** Fallback-centrum för kartan om ingen plats valts (t.ex. fordonets placering). */
  fallbackCenter?: { lat: number; lng: number } | null;
  /** Batterikapacitet uppslagen från Car.info (kWh), om tillgänglig. */
  suggestedBatteryCapacityKwh?: number | null;
  /** Sparad källa i DB (manuell vs Car.info). */
  batteryCapacitySource?: 'manual' | 'car_info' | null;
}

const DEFAULT_HOME_RADIUS = 100;
const DEFAULT_WORK_RADIUS = 150;

function parseNum(raw: string): number | null {
  if (raw.trim() === '') return null;
  const n = Number(raw.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

type PickerKind = 'home' | 'work' | null;

export default function VehicleChargingTab({
  value,
  onChange,
  hasChanges,
  fallbackCenter,
  suggestedBatteryCapacityKwh,
  batteryCapacitySource,
}: VehicleChargingTabProps) {
  const { t } = useTranslation();
  const [picker, setPicker] = useState<PickerKind>(null);

  const update = useCallback(
    (patch: Partial<ChargingConfigFormState>) => onChange({ ...value, ...patch }),
    [onChange, value]
  );

  // Slå upp batterikapacitet automatiskt från Car.info om värde saknas (en gång).
  const autoFilledRef = useRef(false);
  useEffect(() => {
    if (autoFilledRef.current) return;
    if (value.batteryCapacityKwh == null && suggestedBatteryCapacityKwh != null) {
      autoFilledRef.current = true;
      update({ batteryCapacityKwh: suggestedBatteryCapacityKwh });
    }
  }, [value.batteryCapacityKwh, suggestedBatteryCapacityKwh, update]);

  const showSuggestion =
    suggestedBatteryCapacityKwh != null &&
    value.batteryCapacityKwh !== suggestedBatteryCapacityKwh;

  const applyHome = (loc: PickedLocation) =>
    update({
      homeLatitude: loc.lat,
      homeLongitude: loc.lng,
      homeAddress: loc.address,
      homeRadiusM: loc.radius,
    });

  const applyWork = (loc: PickedLocation) =>
    update({
      workLatitude: loc.lat,
      workLongitude: loc.lng,
      workAddress: loc.address,
      workRadiusM: loc.radius,
    });

  const clearHome = () =>
    update({ homeLatitude: null, homeLongitude: null, homeAddress: null, homeRadiusM: null });
  const clearWork = () =>
    update({ workLatitude: null, workLongitude: null, workAddress: null, workRadiusM: null });

  const homeSet = value.homeLatitude != null && value.homeLongitude != null;
  const workSet = value.workLatitude != null && value.workLongitude != null;

  return (
    <div
      className={`space-y-5 p-4 border rounded-lg ${
        hasChanges ? 'border-amber-400 bg-amber-50/50 dark:bg-amber-950/20' : 'border-border'
      }`}
    >
      <p className="text-sm text-textSecondary">{t('charging.config.description')}</p>

      {/* Batteri & laddförlust */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-textSecondary mb-1">
            {t('charging.config.batteryCapacity')}
          </label>
          <input
            type="number"
            min={0}
            step="0.1"
            value={value.batteryCapacityKwh ?? ''}
            onChange={(e) => update({ batteryCapacityKwh: parseNum(e.target.value) })}
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgLight"
          />
          {showSuggestion && (
            <button
              type="button"
              onClick={() => update({ batteryCapacityKwh: suggestedBatteryCapacityKwh })}
              className="mt-1 text-xs text-primary hover:underline"
            >
              {t('charging.config.batteryFromCarInfo', { value: suggestedBatteryCapacityKwh })}
            </button>
          )}
          {batteryCapacitySource === 'manual' && (
            <p className="mt-1 text-xs text-textSecondary">{t('charging.config.batterySourceManual')}</p>
          )}
          {batteryCapacitySource === 'car_info' && (
            <p className="mt-1 text-xs text-textSecondary">{t('charging.config.batterySourceCarInfo')}</p>
          )}
        </div>
        <div>
          <label className="block text-xs text-textSecondary mb-1">
            {t('charging.config.efficiency')}
          </label>
          <input
            type="number"
            min={0}
            max={1}
            step="0.01"
            value={value.chargingEfficiency ?? ''}
            onChange={(e) => update({ chargingEfficiency: parseNum(e.target.value) })}
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgLight"
          />
        </div>
      </div>

      {/* Hem */}
      <LocationSection
        label={t('charging.config.homeLocation')}
        isSet={homeSet}
        address={value.homeAddress}
        lat={value.homeLatitude}
        lng={value.homeLongitude}
        radius={value.homeRadiusM}
        pickLabel={homeSet ? t('charging.config.changeOnMap') : t('charging.config.pickOnMap')}
        onPick={() => setPicker('home')}
        onClear={clearHome}
        clearLabel={t('charging.config.clear')}
        radiusLabel={t('charging.config.radius')}
      />

      {/* Arbete */}
      <LocationSection
        label={t('charging.config.workLocation')}
        isSet={workSet}
        address={value.workAddress}
        lat={value.workLatitude}
        lng={value.workLongitude}
        radius={value.workRadiusM}
        pickLabel={workSet ? t('charging.config.changeOnMap') : t('charging.config.pickOnMap')}
        onPick={() => setPicker('work')}
        onClear={clearWork}
        clearLabel={t('charging.config.clear')}
        radiusLabel={t('charging.config.radius')}
      />

      <p className="text-xs text-textSecondary">{t('charging.config.publicNote')}</p>

      {picker === 'home' && (
        <LocationPickerModal
          title={t('charging.config.homeLocation')}
          initialLat={value.homeLatitude}
          initialLng={value.homeLongitude}
          initialRadius={value.homeRadiusM}
          initialAddress={value.homeAddress}
          fallbackCenter={fallbackCenter ?? null}
          defaultRadius={DEFAULT_HOME_RADIUS}
          onSave={applyHome}
          onClose={() => setPicker(null)}
        />
      )}
      {picker === 'work' && (
        <LocationPickerModal
          title={t('charging.config.workLocation')}
          initialLat={value.workLatitude}
          initialLng={value.workLongitude}
          initialRadius={value.workRadiusM}
          initialAddress={value.workAddress}
          fallbackCenter={fallbackCenter ?? null}
          defaultRadius={DEFAULT_WORK_RADIUS}
          onSave={applyWork}
          onClose={() => setPicker(null)}
        />
      )}
    </div>
  );
}

interface LocationSectionProps {
  label: string;
  isSet: boolean;
  address: string | null;
  lat: number | null;
  lng: number | null;
  radius: number | null;
  pickLabel: string;
  onPick: () => void;
  onClear: () => void;
  clearLabel: string;
  radiusLabel: string;
}

function LocationSection({
  label,
  isSet,
  address,
  lat,
  lng,
  radius,
  pickLabel,
  onPick,
  onClear,
  clearLabel,
  radiusLabel,
}: LocationSectionProps) {
  return (
    <div className="border border-border rounded-lg p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{label}</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onPick}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border rounded hover:bg-bgLight"
          >
            <MapPinIcon className="h-4 w-4" />
            {pickLabel}
          </button>
          {isSet && (
            <button
              type="button"
              onClick={onClear}
              className="inline-flex items-center gap-1 px-2 py-1.5 text-sm text-red-600 border border-red-200 rounded hover:bg-red-50"
            >
              <XMarkIcon className="h-4 w-4" />
              {clearLabel}
            </button>
          )}
        </div>
      </div>

      {isSet ? (
        <div className="text-sm space-y-0.5">
          {address && <div className="text-textPrimary">{address}</div>}
          <div className="text-xs text-textSecondary">
            {lat?.toFixed(6)}, {lng?.toFixed(6)} · {radiusLabel}: {radius ?? '-'} m
          </div>
        </div>
      ) : (
        <div className="text-xs text-textSecondary">—</div>
      )}
    </div>
  );
}
